import { createContext, useContext } from "react";
import { GetServerSidePropsContext } from "next";
import { fetchPage, Page, PageProps } from "@zapier/builder.io-kit";
import {
  Currency,
  geolocatedCurrencyQuery,
  geolocatedCurrencyQueryType,
} from "@zapier/currency";
import { addApolloState, getOrCreateApolloClient } from "@/apollo";
import { DEFAULT_COUNTRY_CODE } from "@/constants";
import { currencyQuery } from "@/hooks";
import { plansQuery } from "@/components/builder/shared/usePlans";
import { CatalogProducts, getCatalogProducts, getIsAuthed } from "@/utils";

type PricingPageContextValues = {
  countryCode?: string;
  geolocatedCurrency?: Currency;
  products?: CatalogProducts;
};

const PricingPageContext = createContext<PricingPageContextValues>({});
export const usePricingPageContext = () => useContext(PricingPageContext);

type Props = {
  countryCode?: string;
  geolocatedCurrency?: Currency;
  content: PageProps["content"];
  products: CatalogProducts;
};

export default function LoggedOutContainer({
  countryCode,
  geolocatedCurrency,
  content,
  products,
}: Props) {
  return (
    <PricingPageContext.Provider
      value={{ countryCode, geolocatedCurrency, products }}
    >
      <Page content={content} serviceName="revenue" />
    </PricingPageContext.Provider>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const isAuthed = getIsAuthed(context);

  if (isAuthed) {
    return {
      redirect: {
        destination: "/app/pricing",
      },
      props: {},
    };
  }

  const content = await fetchPage({
    model: "pricing-page",
    url: "/pricing",
    /**
     *  Builder.io documentation -
     *    - staleCacheSeconds: https://www.builder.io/c/docs/content-api#code-stale-cache-seconds-code
     *    - cacheSeconds: https://www.builder.io/c/docs/content-api#code-cache-seconds-code
     * */
    staleCacheSeconds: 86400,
    cacheSeconds: 60,
  });

  if (!content.data) {
    return {
      notFound: true,
    };
  }

  let data = {};
  const apolloClient = getOrCreateApolloClient(null, context);
  try {
    const countryCode =
      (context?.req?.headers["cloudfront-viewer-country"] as string) ||
      DEFAULT_COUNTRY_CODE;

    const queryResult = await apolloClient.query<geolocatedCurrencyQueryType>({
      query: geolocatedCurrencyQuery,
      variables: {
        countryCode: countryCode,
      },
    });

    const plansRequest = apolloClient.query({
      query: plansQuery,
      variables: { accountId: null },
    });

    const currenciesRequest = apolloClient.query({
      query: currencyQuery,
    });

    // query for plans and currencies to warm the Apollo cache
    const [products] = await Promise.all([
      getCatalogProducts(),
      plansRequest,
      currenciesRequest,
    ]);

    data = {
      countryCode,
      geolocatedCurrency: queryResult.data?.geolocatedCurrency?.currency,
      products,
    };
  } catch (e) {
    console.error(e);
  }

  return addApolloState(apolloClient, {
    props: {
      content,
      ...data,
    },
  });
}
